import { memo, useEffect, useCallback, useMemo } from "react";
import "./css/Sidebar.css";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const menuItems = [
  { path: "/updates", label: "Updates" },
  { path: "/dashboard", label: "Dashboard" },
  { path: "/event-booking", label: "Events" },
  { path: "/partners", label: "Partners" },
  { path: "/messages", label: "Messages" },
  { path: "/connections", label: "Connections" },
  { path: "/contact-us", label: "Contact Us" },
  { path: "/my-account", label: "My Account" },
];

function Sidebar({ isOpen, toggleSidebar }: SidebarProps) {
  const { instance } = useMsal();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(async () => {
    localStorage.clear();
    await instance.logoutRedirect();
  }, [instance]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (window.innerWidth < 1024) {
        toggleSidebar();
      }
    },
    [navigate, toggleSidebar]
  );

  const isActive = useCallback(
    (path: string) => location.pathname.startsWith(path),
    [location.pathname]
  );

  useEffect(() => {
    if (!isOpen || window.innerWidth >= 1024) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const sidebarElement = document.querySelector(".sidebar");
      const toggleButton = document.querySelector(".navbar-toggler");

      if (
        sidebarElement &&
        !sidebarElement.contains(event.target as Node) &&
        toggleButton &&
        !toggleButton.contains(event.target as Node)
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  const isAdmin = useMemo(() => user?.user?.isAdmin === 1, [user?.user?.isAdmin]);

  return (
    <div className={`sidebar gradient-bg text-white ${isOpen ? "open" : ""}`}>
      <ul className="navbar-nav me-auto py-3">
        {menuItems.map((item) => (
          <li key={item.path} className="nav-item btn-bg">
            <button
              className={`nav-link w-100 ${isActive(item.path) ? "active" : ""}`}
              onClick={() => handleNavigate(item.path)}
            >
              {item.label}
            </button>
          </li>
        ))}
        {isAdmin && (
          <li className="nav-item btn-bg">
            <button
              className={`nav-link w-100 ${isActive("/admin") ? "active" : ""}`}
              onClick={() => handleNavigate("/admin")}
            >
              Admin
            </button>
          </li>
        )}
      </ul>
      <div className="ms-auto d-lg-none login-button">
        <button
          className="nav-link border text-center w-100 btn-bg border-white rounded px-3 py-1 mt-2"
          onClick={handleLogout}
        >
          Logout
        </button>
      </div>
    </div>
  );
}

Sidebar.displayName = "Sidebar";

export default memo(Sidebar);
